import { type JSX, useMemo } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Spinner } from '@heroui/react';
import clsx from 'clsx';

import { useFrameworkManifest } from '~/hooks/useFrameworks';

import type { FrameworkManifestEntry } from '~/types';

import { FrameworkCard } from './components/FrameworkCard';
import { TEST_LAYER_LABELS, TEST_LAYER_ORDER } from './utils';

interface TestLayerGroup {
  entries: FrameworkManifestEntry[];
  label: string;
  layer: string;
}

export function FrameworksHome(): JSX.Element {
  const { error, isLoading, manifest } = useFrameworkManifest();

  const groups = useMemo((): TestLayerGroup[] => {
    if (!manifest) return [];
    const byLayer = new Map<string, FrameworkManifestEntry[]>();
    for (const fw of manifest.frameworks) {
      const existing = byLayer.get(fw.test_layer) ?? [];
      existing.push(fw);
      byLayer.set(fw.test_layer, existing);
    }

    const result: TestLayerGroup[] = [];
    for (const layer of TEST_LAYER_ORDER) {
      const entries = byLayer.get(layer);
      if (entries && entries.length > 0) {
        result.push({
          layer,
          label: TEST_LAYER_LABELS[layer] ?? layer,
          entries,
        });
      }
    }
    // Catch any layers not in the ordering
    for (const [layer, entries] of byLayer.entries()) {
      if (!TEST_LAYER_ORDER.includes(layer)) {
        result.push({
          layer,
          label: TEST_LAYER_LABELS[layer] ?? layer,
          entries,
        });
      }
    }
    return result;
  }, [manifest]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center text-error">
        Failed to load frameworks. Please try again later.
      </div>
    );
  }

  const totalFrameworks = manifest?.frameworks.length ?? 0;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Frameworks</h1>
        <p className="text-sm text-muted mt-1">
          {totalFrameworks} operational guides for test execution
        </p>
      </div>

      <div className="space-y-6">
        {groups.map(group => (
          <section key={group.layer}>
            <div className="flex items-center gap-2 px-4 py-3 rounded-lg bg-surface-elevated">
              <ChevronDownIcon
                className={clsx('h-4 w-4 text-muted transition-transform flex-shrink-0')}
              />
              <h2 className="text-base font-semibold text-foreground">{group.label}</h2>
              <span className="text-xs text-muted">({group.entries.length})</span>
            </div>
            <div className="mt-3 ml-2 grid grid-cols-1 md:grid-cols-3 gap-4">
              {group.entries.map(fw => (
                <FrameworkCard key={fw.framework_id} framework={fw} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
